import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, Button, Heading, HStack, Select, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useAuthenticatedRequest } from "../../hooks/useAuthenticatedRequest";
import { useAuth } from "../../AuthContext";
import { usePartyNames } from "../../hooks/usePartyNames";
import { getUserOrganisationIds, isUserSuperAdmin } from "../../utils/ownership";
import { unwrapApiResponse } from "../../utils/unwrapApiResponse";
import { showApiErrorToast } from "../../utils/showApiToast";
import { BASE_URL as MRCAI_BASE } from "../../services/MrcaiAPI";

const CODIFY_BASE = "https://codify.poc.mrc.ai/api";
export const PUBLIC_ORGANISATION_ID = "00000000-0000-0000-0000-000000000001";
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
const raw = (v) => v?.$date ?? v;

/** Specifications that fit the product's contract schema (all of them when it has none). */
export const fitsContractSchema = (spec, contractSchema) => {
  if (!contractSchema?.id) return true;
  if (spec.contractSchemaId !== contractSchema.id) return false;
  return !contractSchema.version || !spec.contractSchemaVersion || String(spec.contractSchemaVersion) === String(contractSchema.version);
};

/** Pick a specification for an organisation and save it (or remove it). */
const SetDefault = ({ specs, current, onSave, isBusy, label }) => {
  const [value, setValue] = useState(current || "");
  useEffect(() => setValue(current || ""), [current]);
  return (
    <HStack>
      <Select size="sm" bg="white" maxW="360px" value={value} onChange={(e) => setValue(e.target.value)} isDisabled={!specs}>
        <option value="">{specs ? "No default" : "Loading…"}</option>
        {(specs || []).map((s) => <option key={s._id} value={s._id}>{s.name || s._id}</option>)}
      </Select>
      <Button size="sm" colorScheme="blue" onClick={() => onSave(value)} isLoading={isBusy} isDisabled={!specs || value === (current || "")}>
        {label}
      </Button>
    </HStack>
  );
};

/**
 * A product's default Codify specifications: each organisation's (only
 * yours come back) and the Public one used by everyone else. You set your
 * own organisation's; super admins can set any, including Public.
 */
const ProductCodifySpecifications = ({ product, onEntriesChange }) => {
  const toast = useToast();
  const { user } = useAuth();
  const { get, put } = useAuthenticatedRequest();
  const request = useRef({ get, put });
  request.current = { get, put };

  const [entries, setEntries] = useState(product?.codifySpecifications || []);
  const [specs, setSpecs] = useState(null);
  const [busy, setBusy] = useState(null);
  const myOrgIds = getUserOrganisationIds(user);
  const [orgId, setOrgId] = useState(myOrgIds[0] || "");
  const superAdmin = isUserSuperAdmin(user);
  const names = usePartyNames([...entries.map((e) => e.organisationId), ...myOrgIds].filter((id) => id !== PUBLIC_ORGANISATION_ID));

  useEffect(() => setEntries(product?.codifySpecifications || []), [product?.codifySpecifications]);

  useEffect(() => {
    request.current
      .get(`${CODIFY_BASE}/specifications?page=1&pageSize=200`)
      .then((res) => setSpecs(asList(unwrapApiResponse(res.data))))
      .catch((err) => {
        setSpecs([]);
        showApiErrorToast(toast, err, { title: "Couldn't load the specifications" });
      });
  }, [toast]);

  const fitting = useMemo(() => (specs ? specs.filter((s) => fitsContractSchema(s, product?.contractSchema)) : null), [specs, product?.contractSchema]);

  const reload = async () => {
    const res = await request.current.get(`${MRCAI_BASE}/products/${encodeURIComponent(product._id)}`);
    const next = unwrapApiResponse(res.data)?.codifySpecifications || [];
    setEntries(next);
    onEntriesChange?.(next);
  };

  const save = async (organisationId, specificationId) => {
    const spec = (specs || []).find((s) => s._id === specificationId);
    setBusy(organisationId);
    try {
      await request.current.put(`${MRCAI_BASE}/products/${encodeURIComponent(product._id)}/codify-specifications/${encodeURIComponent(organisationId)}`, {
        specificationId: specificationId || "",
        specificationName: spec?.name || "",
      });
      toast({ title: specificationId ? "Default specification saved" : "Default removed", status: "success", duration: 2000 });
      await reload();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't save the default specification" });
    } finally {
      setBusy(null);
    }
  };

  const orgName = (id) => (id === PUBLIC_ORGANISATION_ID ? "Public (everyone else)" : names[id] || "…");
  const entryFor = (id) => entries.find((e) => e.organisationId === id);
  const sorted = [...entries].sort((a, b) => (a.organisationId === PUBLIC_ORGANISATION_ID) - (b.organisationId === PUBLIC_ORGANISATION_ID));

  return (
    <Box borderWidth="1px" borderRadius="xs" p={4} mb={2} borderColor="gray.500" backgroundColor="whiteAlpha.800">
      <HStack justify="space-between" mb={1}>
        <Heading size="md">Codify specifications</Heading>
        {product?.contractSchema?.id && (
          <Badge textTransform="none">for {product.contractSchema.id}{product.contractSchema.version ? ` v${product.contractSchema.version}` : ""}</Badge>
        )}
      </HStack>
      <Text fontSize="sm" color="gray.600" mb={3}>
        The specification each organisation's contracts on this product are codified with. Organisations without one use the Public default.
      </Text>

      {!product?._id ? (
        <Text fontSize="sm" color="gray.500">Save the product first.</Text>
      ) : (
        <>
          {sorted.length === 0 ? (
            <Text fontSize="sm" color="gray.500" mb={3}>No defaults set yet.</Text>
          ) : (
            <Table size="sm" mb={4}>
              <Thead><Tr><Th>Organisation</Th><Th>Specification</Th><Th>Updated</Th></Tr></Thead>
              <Tbody>
                {sorted.map((e) => (
                  <Tr key={e.organisationId}>
                    <Td>{orgName(e.organisationId)}</Td>
                    <Td>{e.specificationName || e.specificationId}</Td>
                    <Td fontSize="xs" color="gray.500">{raw(e.updatedDate) ? new Date(raw(e.updatedDate)).toLocaleString() : ""}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {myOrgIds.length > 0 && (
            <Box mb={superAdmin ? 4 : 0}>
              <HStack mb={2} spacing={2}>
                <Text fontSize="sm" fontWeight="semibold">Set my organisation's default</Text>
                {myOrgIds.length > 1 && (
                  <Select size="xs" w="auto" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                    {myOrgIds.map((id) => <option key={id} value={id}>{names[id] || id}</option>)}
                  </Select>
                )}
                {myOrgIds.length === 1 && <Text fontSize="sm" color="gray.600">({names[orgId] || "…"})</Text>}
              </HStack>
              <SetDefault specs={fitting} current={entryFor(orgId)?.specificationId} onSave={(id) => save(orgId, id)} isBusy={busy === orgId} label="Save" />
            </Box>
          )}

          {superAdmin && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Public default</Text>
              <SetDefault specs={fitting} current={entryFor(PUBLIC_ORGANISATION_ID)?.specificationId}
                onSave={(id) => save(PUBLIC_ORGANISATION_ID, id)} isBusy={busy === PUBLIC_ORGANISATION_ID} label="Save" />
            </Box>
          )}
          {fitting && specs && fitting.length < specs.length && (
            <Text fontSize="xs" color="gray.500" mt={2}>Only specifications for this product's contract schema are offered.</Text>
          )}
          {fitting === null && <Spinner size="sm" />}
        </>
      )}
    </Box>
  );
};

export default ProductCodifySpecifications;
